import { Component, OnInit } from '@angular/core';
import { handlebars } from '@components/singletons/handlebars/handlebars';
import { ICON } from '@core/util/icon';
import {
	NameFormattingCaseTypes,
	NameFormattingSettings,
	NameFormattingTemplateOptions,
} from '@models/businessSettings/settings';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import {
	getIndividualFirstName,
	getIndividualLastName,
	getIndividualMiddleNames,
} from '@core/util/name-formatting.utils';
import { BusinessDispatch, BusinessState } from '@state/business';
import { Observable } from 'rxjs';
import { ButtonGroupOption } from 'src/app/shared/atomic/atoms/button-group/button-group.component';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';

enum NameFormattingTabOptions {
	INDIVIDUAL = 'INDIVIDUAL',
	COMPANY = 'COMPANY',
}

@UntilDestroy()
@Component({
	selector: 'app-name-formatting-settings',
	templateUrl: './name-formatting.component.html',
	standalone: false,
})
export class NameFormattingComponent implements OnInit {
	@Select(BusinessState.getNameFormattingSettings)
	nameFormattingSetting$: Observable<NameFormattingSettings>;

	nameFormattingSettings: NameFormattingSettings;

	tabs: ButtonGroupOption<string>[] = [
		{
			label: 'Individual',
			value: NameFormattingTabOptions.INDIVIDUAL,
		},
		{
			label: 'Company',
			value: NameFormattingTabOptions.COMPANY,
		},
	];

	tabOptions = NameFormattingTabOptions;
	selectedTab: NameFormattingTabOptions = NameFormattingTabOptions.INDIVIDUAL;
	ICON = ICON;
	caseTypeOptions: RadioGroupItem<NameFormattingCaseTypes>[] = [
		{
			label: 'As typed',
			value: NameFormattingCaseTypes.AS_TYPED,
		},
		{
			label: 'Titlecase',
			value: NameFormattingCaseTypes.TITLECASE,
		},
		{
			label: 'Uppercase',
			value: NameFormattingCaseTypes.UPPERCASE,
		},
	];

	parsedNameFormattingOptions: DropdownOption<string>[] = [];

	selectedFormat: string = '';

	constructor(private businessDispatch: BusinessDispatch) {}

	ngOnInit(): void {
		this.nameFormattingSetting$
			.pipe(untilDestroyed(this))
			.subscribe((settings) => {
				this.parsedNameFormattingOptions = [];

				this.nameFormattingSettings = settings;
				this.selectedFormat = this.getParsedName(
					NameFormattingTemplateOptions[
						this.nameFormattingSettings.individual.template
					],
				);

				Object.entries(NameFormattingTemplateOptions)
					.reverse()
					.forEach(([key, format]) => {
						this.parsedNameFormattingOptions.push({
							name: this.getParsedName(format),
							value: key,
						});
					});
			});
	}

	getParsedName(format) {
		const placeholderFirstName = getIndividualFirstName(
			'John',
			this.nameFormattingSettings,
		);
		const placeholderMiddleNames = getIndividualMiddleNames(
			'Parker',
			this.nameFormattingSettings,
		);
		const placeholderLastName = getIndividualLastName(
			'Smith',
			this.nameFormattingSettings,
		);

		return handlebars.compile(format, {
			firstName: placeholderFirstName,
			middleNames: placeholderMiddleNames,
			lastName: placeholderLastName,
		});
	}

	onSelectFormat(value: DropdownResult<DropdownOption<string>>) {
		this.businessDispatch.UpdateDefaultSettings({
			nameFormatting: {
				individual: {
					template: value.result.value,
				},
			},
		});
	}

	setFirstNameCaseType(value: NameFormattingCaseTypes) {
		this.businessDispatch.UpdateDefaultSettings({
			nameFormatting: {
				individual: {
					firstName: {
						caseStyle: value,
					},
				},
			},
		});
	}

	setLastNameCaseType(value: NameFormattingCaseTypes) {
		this.businessDispatch.UpdateDefaultSettings({
			nameFormatting: {
				individual: {
					lastName: {
						caseStyle: value,
					},
				},
			},
		});
	}

	setMiddleNamesCaseType(value: NameFormattingCaseTypes) {
		this.businessDispatch.UpdateDefaultSettings({
			nameFormatting: {
				individual: {
					middleNames: {
						caseStyle: value,
					},
				},
			},
		});
	}

	setBusinessNameCaseType(value: NameFormattingCaseTypes) {
		this.businessDispatch.UpdateDefaultSettings({
			nameFormatting: {
				company: {
					caseStyle: value,
				},
			},
		});
	}
}
